
import { GameClient } from "../engine/GameClient";
import { GameClientSocket } from "../networking/GameClientSocket";
import {
  GameOptions,
  GameState,
} from "../../core/protocol/types";
import { Packet } from "../../core/protocol/packets";

/**
 * GameController manages the client-side game session.
 * It handles the lifecycle of GameClient and GameClientSocket.
 * 
 * Refactored for Client-Server Architecture.
 */
export class GameController {
  private client?: GameClient;
  private socket?: GameClientSocket;
  private updateCallback: (state: GameState) => void;
  private onJoinRejected?: (reason: string) => void;
  private onErrorCallback?: (error: string) => void;
  private packetQueueCallback?: (packet: Packet) => void;
  private remoteGameManager?: any;

  // Legacy properties to satisfy interfaces if needed, but shouldn't be used
  // private network: any; 

  constructor(updateCallback: (state: GameState) => void) {
    this.updateCallback = updateCallback;
  }

  setOnJoinRejected(callback: (reason: string) => void): void {
    this.onJoinRejected = callback;
  }

  setOnError(callback: (error: string) => void): void {
    this.onErrorCallback = callback;
  }

  /**
   * Connect to a game (used by JoinRuntimeSession)
   * @param wsUrl WebSocket URL to connect to
   */
  async connectToGame(wsUrl: string): Promise<void> {
     if (this.socket) {
         this.socket.disconnect();
     }
     
     this.socket = new GameClientSocket(wsUrl);
     if (this.onErrorCallback) {
         this.socket.onError = (e) => this.onErrorCallback!(e.toString());
     }

     this.client = new GameClient(
         this.socket,
         this.updateCallback,
         this.onJoinRejected
     );

     if (this.packetQueueCallback) {
         this.client.setQueuePacketCallback(this.packetQueueCallback);
     }

     if (this.remoteGameManager) {
         this.client.setRemoteGameManager(this.remoteGameManager);
     }

     // Connects but doesn't join yet
     await this.client.connectToGame(wsUrl);
  }

  /**
   * Join a game (Connect + WANT_JOIN)
   */
  async joinGame(hostId: string, playerName: string, wsUrl?: string): Promise<void> {
    const url = wsUrl || hostId;
    
    if (!this.socket || this.socket.url !== url) {
        await this.connectToGame(url);
    }

    if (this.client) {
        await this.client.joinGame(url, playerName);
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    if (this.client) {
      this.client.destroy();
      this.client = undefined;
    }
    if (this.socket) {
      this.socket.disconnect();
      this.socket = undefined;
    }
  }

  // Proxy methods to client
  
  public handlePlayerInput(input: any): void {
      this.client?.handlePlayerInput(input);
  }

  public requestPauseToggle(): void {
      this.client?.requestPauseToggle();
  }

  public changeTeam(team: number): void {
      this.client?.sendChangeTeam(team);
  }

  public updateGameOptions(options: Partial<GameOptions>): void {
      this.client?.updateGameOptions(options);
  }

  public sendAttack(attack: any): void {
      this.client?.sendAttack(attack);
  }
  

  public sendClientState(state: any): void {
      this.client?.sendClientState(state);
  }

  public sendClientFirstFrag(): void {
      this.client?.sendClientFirstFrag();
  }

  public sendRespawnRequest(): void {
      this.client?.sendRespawnRequest();
  }

  public getPeerId(): string {
      // Return local connection ID if available, or empty
      return this.client?.getState().localConnectionId || "";
  }
  
  public setQueuePacketCallback(cb: (packet: Packet) => void): void {
      this.packetQueueCallback = cb;
      if (this.client) {
          this.client.setQueuePacketCallback(cb);
      }
  }

  public getGameState(): GameState | null {
      return this.client ? this.client.getState() : null;
  }

  public setRemoteGameManager(manager: any): void {
      this.remoteGameManager = manager;
      this.client?.setRemoteGameManager(manager);
  }

  public processQueuedServerStatePackets(): void {
      this.client?.processQueuedServerStatePackets();
  }

  public updatePlayerState(playerState: any): void {
      this.client?.updatePlayerState(playerState);
  }

  public getPendingPackets(): any[] {
      return this.client?.getPendingPackets() || [];
  }

  public setGameStatus(status: string): void {
      if (this.client && this.client.getState()) {
          this.client.getState().status = status as any;
      }
  }
  
  public sendMoves(moves: any[], numPlayer?: any): void {
      this.client?.sendMoves(moves, numPlayer);
  }
  
  public notifyPlayerDeath(thenGone?: boolean, attackerNumPlayer?: number): void {
      this.client?.notifyPlayerDeath(thenGone || false, attackerNumPlayer);
  }
  
  public sendStampBlock(x: any, y?: any, rotation?: any, blockType?: any): void {
      // Handle both array overload and individual args
      if (Array.isArray(x)) {
          this.client?.sendStampBlock(x);
      } else {
          this.client?.sendStampBlockArgs(x, y, rotation, blockType);
      }
  }
}
